'use client';
import { useEffect, useState, useCallback } from 'react';
import Link from 'next/link';
import { useT } from '@/hooks/useT';

interface Review {
  id: number;
  rating: number;
  body: string | null;
  created_at: string;
  display_name: string | null;
  username: string | null;
  avatar_url: string | null;
  profile_code: string | null;
  customer_id: number;
}

interface ReviewsData {
  reviews: Review[];
  avg: number | null;
  count: number;
  my_review: { id: number; rating: number; body: string | null } | null;
  page: number;
}

function StarFill({ filled, size = 14 }: { filled: boolean; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24"
      fill={filled ? '#fbbf24' : 'none'}
      stroke={filled ? '#fbbf24' : 'var(--v4-border-strong)'}
      strokeWidth="1.5">
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
    </svg>
  );
}

function Stars({ rating, size = 14 }: { rating: number; size?: number }) {
  return (
    <span style={{ display: 'inline-flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map(n => <StarFill key={n} filled={n <= rating} size={size} />)}
    </span>
  );
}

function Avatar({ name, url }: { name: string | null; url: string | null }) {
  const initials = (name || '?').slice(0, 2).toUpperCase();
  if (url) return (
    <img src={url} alt={name || ''} style={{
      width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
    }} />
  );
  return (
    <div style={{
      width: 38, height: 38, borderRadius: '50%',
      background: 'linear-gradient(135deg,rgba(82,176,67,.18),rgba(0,120,212,.18))',
      border: '1px solid var(--v4-border)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 13, fontWeight: 700, color: 'var(--v4-fg-3)', flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

export function ReviewsPanel({ productId, visible }: { productId: string; visible: boolean }) {
  const t = useT();
  const [data, setData] = useState<ReviewsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/reviews?product_id=${productId}&page=${p}`);
      const json = await res.json();
      setData(json);
      setPage(p);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => { if (visible && !data) load(1); }, [visible, data, load]);

  if (!visible) return null;

  const totalPages = data ? Math.ceil(data.count / 10) : 1;
  const avg = data?.avg ? Number(data.avg) : null;

  const starCounts = [5, 4, 3, 2, 1].map(s => ({
    star: s,
    count: data?.reviews.filter(r => r.rating === s).length ?? 0,
  }));
  const maxCount = Math.max(...starCounts.map(s => s.count), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>

      {/* -- Loading skeleton -- */}
      {loading && !data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: '8px 0' }}>
          {[1, 2, 3].map(i => (
            <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--v4-panel-2)', flexShrink: 0 }} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ width: '30%', height: 9, borderRadius: 4, background: 'var(--v4-panel-2)' }} />
                <div style={{ width: '80%', height: 9, borderRadius: 4, background: 'var(--v4-panel)' }} />
                <div style={{ width: '55%', height: 9, borderRadius: 4, background: 'var(--v4-panel)' }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* -- Aggregate header -- */}
      {data && data.count > 0 && (
        <div style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
          {/* Big score */}
          <div style={{ textAlign: 'center', flexShrink: 0 }}>
            <div style={{
              fontSize: 52, fontWeight: 900, lineHeight: 1, color: 'var(--v4-fg)',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {avg ? avg.toFixed(1) : '—'}
            </div>
            <div style={{ marginTop: 6 }}>
              <Stars rating={Math.round(avg ?? 0)} size={15} />
            </div>
            <div style={{ fontSize: 11, color: 'var(--v4-fg-4)', marginTop: 5, letterSpacing: '.02em' }}>
              {data.count}&nbsp;{t('review_label')}
            </div>
          </div>

          {/* Distribution bars */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {starCounts.map(({ star, count }) => {
              const pct = (count / maxCount) * 100;
              return (
                <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    fontSize: 11, color: 'var(--v4-fg-4)',
                    width: 14, textAlign: 'right', flexShrink: 0,
                    fontVariantNumeric: 'tabular-nums',
                  }}>{star}</span>
                  <svg width={10} height={10} viewBox="0 0 24 24" fill="#fbbf24" strokeWidth="0" style={{ flexShrink: 0, opacity: .6 }}>
                    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                  </svg>
                  <div style={{ flex: 1, height: 4, background: 'var(--v4-panel-2)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{
                      width: `${pct}%`, height: '100%', borderRadius: 2,
                      background: pct > 60 ? 'var(--gs-green)' : pct > 30 ? 'rgba(251,191,36,.7)' : 'var(--v4-fg-4)',
                      transition: 'width .45s cubic-bezier(.4,0,.2,1)',
                    }} />
                  </div>
                  <span style={{
                    fontSize: 11, color: 'var(--v4-fg-4)',
                    width: 18, textAlign: 'right', flexShrink: 0,
                    fontVariantNumeric: 'tabular-nums',
                  }}>{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* -- Empty state -- */}
      {data && data.count === 0 && (
        <div style={{ padding: '36px 0 12px', textAlign: 'center' }}>
          <div style={{ display: 'flex', gap: 3, justifyContent: 'center', marginBottom: 12 }}>
            {[1,2,3,4,5].map(n => (
              <svg key={n} width={20} height={20} viewBox="0 0 24 24"
                fill="none" stroke="var(--v4-border-strong)" strokeWidth="1.5">
                <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
              </svg>
            ))}
          </div>
          <div style={{ fontSize: 13, color: 'var(--v4-fg-4)' }}>{t('pdp_no_reviews')}</div>
          <div style={{ fontSize: 11, color: 'var(--v4-fg-4)', marginTop: 6 }}>
            {t('pdp_reviews_buy')}&nbsp;<a href="/account/orders" style={{ color: 'var(--gs-green)', textDecoration: 'none' }}>{t('pdp_reviews_orders')}</a>
          </div>
        </div>
      )}

      {/* -- Reviews list -- */}
      {data && data.reviews.length > 0 && (
        <>
          <div style={{ height: 1, background: 'var(--v4-panel-2)', margin: '-8px 0' }} />

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {data.reviews.map((r, i) => (
              <div key={r.id} style={{
                display: 'flex', gap: 14, alignItems: 'flex-start',
                padding: '18px 0',
                borderBottom: i < data.reviews.length - 1 ? '1px solid var(--v4-border)' : 'none',
              }}>
                <Avatar name={r.display_name || r.username} url={r.avatar_url} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                    {r.profile_code ? (
                      <Link href={`/user/${r.profile_code}`} style={{
                        fontSize: 13, fontWeight: 700, color: 'var(--v4-fg)',
                        textDecoration: 'none', transition: 'color .15s',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.color = 'var(--gs-green)')}
                      onMouseLeave={e => (e.currentTarget.style.color = 'var(--v4-fg)')}>
                        {r.display_name || r.username || t('review_label')}
                      </Link>
                    ) : (
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--v4-fg)' }}>
                        {r.display_name || r.username || t('review_label')}
                      </span>
                    )}
                    <Stars rating={r.rating} size={12} />
                    <span style={{
                      marginLeft: 'auto', fontSize: 11,
                      color: 'var(--v4-fg-4)',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {new Date(r.created_at).toLocaleDateString('uk-UA', {
                        day: 'numeric', month: 'short', year: 'numeric',
                      })}
                    </span>
                  </div>
                  {r.body && (
                    <p style={{
                      margin: 0, fontSize: 13,
                      color: 'var(--v4-fg-2)',
                      lineHeight: 1.65,
                    }}>
                      {r.body}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <button
                onClick={() => load(page - 1)}
                disabled={page <= 1}
                style={{
                  padding: '6px 14px', borderRadius: 7,
                  border: '1px solid var(--v4-border)',
                  background: 'none',
                  color: page > 1 ? 'var(--v4-fg-3)' : 'var(--v4-fg-4)',
                  cursor: page > 1 ? 'pointer' : 'default', fontSize: 12,
                }}
              >← {t('hero_prev')}</button>
              <span style={{ fontSize: 11, color: 'var(--v4-fg-4)', padding: '0 4px' }}>
                {page} / {totalPages}
              </span>
              <button
                onClick={() => load(page + 1)}
                disabled={page >= totalPages}
                style={{
                  padding: '6px 14px', borderRadius: 7,
                  border: '1px solid var(--v4-border)',
                  background: 'none',
                  color: page < totalPages ? 'var(--v4-fg-3)' : 'var(--v4-fg-4)',
                  cursor: page < totalPages ? 'pointer' : 'default', fontSize: 12,
                }}
              >{t('hero_next')} →</button>
            </div>
          )}

          {/* Leave review hint */}
          <div style={{
            padding: '13px 16px',
            background: 'rgba(82,176,67,.05)',
            border: '1px solid rgba(82,176,67,.1)',
            borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          }}>
            <span style={{ fontSize: 12, color: 'var(--v4-fg-3)' }}>
              {t('review_label')}
            </span>
            <a
              href="/account/orders"
              style={{
                fontSize: 12, fontWeight: 700, color: 'var(--gs-green)',
                textDecoration: 'none', whiteSpace: 'nowrap',
                display: 'inline-flex', alignItems: 'center', gap: 5,
              }}
            >
              <svg width={11} height={11} viewBox="0 0 24 24" fill="var(--gs-green)" strokeWidth="0">
                <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
              </svg>
              {t('review_leave')}
            </a>
          </div>
        </>
      )}
    </div>
  );
}
